import React, { use, useMemo } from "react";
import {
    ContractSignatureStatus,
    ContractState,
    ProfileType,
    TerminatedByType,
    TerminationInfo,
} from "../../__generated__/graphql";
import { CoreMissionFieldsFragment } from "../../__generated__/operations/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import Label from "../../ui_atoms/Label";
import { useIntercom } from "../../hooks/intercom";
import { useContractSigningInfo } from "../../hooks/contacts";
import Text from "../Text";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet } from "react-native";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { parseISO } from "date-fns";
import { formatDate, SYSTEM_NAME } from "../../utils";
import { useNavigation } from "@react-navigation/core";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import Callout from "../../ui_atoms/Callout";

type MissionDetailsStatusProps = {
    mission: CoreMissionFieldsFragment;
    terminationInfo?: TerminationInfo;
};

type ReasonTextProps = {
    title?: string;
    text?: string | null;
    color?: string;
};

const ReasonText = ({ title, text, color }: ReasonTextProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    if (!text) {
        return null;
    }
    return (
        <VStack top={0.5}>
            <Text style={[styles.reasonTitleText, { color }]}>
                {title || i18n.t("screens.termination.reason.other_description")}
            </Text>
            <Text style={[styles.reasonDescription, { color }]}>&quot;{text}&quot;</Text>
        </VStack>
    );
};

type ScreenParams = StackScreenProps<RootStackParamList>;

const MissionDetailsStatus: React.FC<MissionDetailsStatusProps> = ({
    mission,
    terminationInfo,
}) => {
    const i18n = useI18n();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { colors } = useTheme();
    const { presentIntercom, presentArticleId } = useIntercom();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { getStatusText } = useContractSigningInfo();
    const currentContract = useMemo(() => mission.currentContract, [mission.currentContract]);
    const signingStatus = currentContract?.signatureStatus;
    const currentContractState = currentContract?.state;

    const styles = useStyles();

    const signingFailed = useMemo(() => {
        return signingStatus === ContractSignatureStatus.Failed;
    }, [signingStatus]);

    const terminatedByName = useMemo(() => {
        switch (currentContract?.terminatedBy) {
            case TerminatedByType.System:
                return SYSTEM_NAME;
            case TerminatedByType.Client:
                return mission.clientInfo.name;
            case TerminatedByType.Worker:
                return mission.workerInfo.name;
        }
    }, [currentContract?.terminatedBy, mission.clientInfo.name, mission.workerInfo.name]);

    const getMissionDisplayAttributes = useMissionDisplayAttributes();
    const displayAttributes = useMemo(
        () => getMissionDisplayAttributes(mission, profileType, terminationInfo),
        [getMissionDisplayAttributes, mission, profileType, terminationInfo],
    );

    const contractStateLabelProps = useMemo(() => {
        if (mission.currentContract?.state !== ContractState.PendingAcceptation) {
            return {
                text: displayAttributes.runningStatus,
                mode: displayAttributes.runningStatuslabelMode,
            };
        }
    }, [
        displayAttributes.runningStatus,
        displayAttributes.runningStatuslabelMode,
        mission.currentContract?.state,
    ]);

    return (
        <>
            <VStack gap={0.25}>
                {contractStateLabelProps && (
                    <Label {...contractStateLabelProps} style={styles.label} />
                )}

                {signingFailed && (
                    <Callout
                        mode="error"
                        markdown={i18n.t("screens.user.contract_details.signing_failed")}
                        onLinkPress={() => {
                            void presentIntercom();
                            return true;
                        }}
                    />
                )}
                {/* we display signature status if contract is not fully signed and not canceled. Because canceled cannot be sign anymore */}
                {!signingFailed &&
                currentContractState !== ContractState.Cancelled &&
                signingStatus !== ContractSignatureStatus.Success ? (
                    <Label
                        mode="warning"
                        text={getStatusText(currentContract)}
                        style={styles.label}
                        multiline
                        {...(profileType === ProfileType.Client &&
                        currentContract?.signatureStatus !== ContractSignatureStatus.Success
                            ? {
                                  linkText: i18n.t(
                                      "screens.user.contract_details.discover_contract_steps",
                                  ),
                                  onPress: () => {
                                      void presentArticleId("9983409");
                                  },
                              }
                            : {})}
                    />
                ) : null}
                {currentContract?.terminationOtherReason &&
                    currentContract.effectiveEndDate &&
                    terminatedByName && (
                        <ReasonText
                            color={colors.error}
                            title={i18n.t(
                                currentContract.state === ContractState.Terminated
                                    ? "screens.termination.reason.termination_date_by"
                                    : "screens.termination.reason.cancelation_date_by",
                                {
                                    terminationDate: formatDate(
                                        parseISO(currentContract.effectiveEndDate),
                                    ),
                                    terminatedBy: terminatedByName,
                                },
                            )}
                            text={currentContract.terminationOtherReason}
                        />
                    )}
                {/* The pending-cancellation reminder is always shown to the client (mission owner).
                    The worker only sees it for a request they initiated themselves, so a
                    client-initiated cancellation is not surfaced to them here. */}
                {terminationInfo?.cancellationRequest &&
                    ![
                        ContractState.Cancelled,
                        ContractState.Terminated,
                        ContractState.Ended,
                    ].includes(currentContractState as ContractState) &&
                    terminationInfo.cancellationRequest.isCancelledBySelf && (
                        <Label
                            mode="warning"
                            style={styles.label}
                            linkText={i18n.t("screens.user.contract_details.pending_termination")}
                            onPress={() => {
                                navigation?.navigate("MissionTermination", {
                                    missionId: mission.id,
                                });
                            }}
                        />
                    )}
                {displayAttributes.hasPendingCurrentUserSignatureForAmendment && (
                    <Label
                        mode="warning"
                        style={styles.label}
                        text={i18n.t(
                            "screens.user.contract_details.amendment_awaiting_your_signature",
                        )}
                    />
                )}
                {displayAttributes.hasPendingWorkerSignatureForAmendment && (
                    <Label
                        mode="warning"
                        style={styles.label}
                        text={i18n.t(
                            "screens.user.contract_details.amendment_awaiting_worker_signature",
                        )}
                    />
                )}
            </VStack>
        </>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                reasonTitleText: {
                    ...fonts.bodyLarge,
                    fontSize: 14,
                    textAlign: "center",
                },
                reasonDescription: {
                    ...fonts.bodySmall,
                    textAlign: "center",
                },
                label: {
                    textAlign: "center",
                },
            }),
        [fonts.bodyLarge, fonts.bodySmall],
    );
};

export default MissionDetailsStatus;
